import { FileDown, FileText } from 'lucide-react'
import type { ReactNode } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  APPOINTMENT_TYPES_BY_ID,
  formatCalendarDate,
  formatInstantDate,
  formatName,
  formatTemperature,
  formatWeight,
  type GetPetRecordsResponse,
  getClinic,
  NOTE_TYPE_LABELS,
  type PatientHistoryEntry,
  type PatientHistoryNote,
  type PatientHistoryTreatment,
  type PatientHistoryVitals,
  possessive,
  REGION_IDS,
  type RegionId,
  resolveDateFormat,
} from '@snootsvet/shared/portal'

import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { NotFound } from '@/customer/components/not-found'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useDownloadPetHistory } from '@/customer/hooks/use-download-pet-history'
import { useGetPetRecords } from '@/customer/hooks/use-get-pet-records'
import { routes } from '@/customer/routes'

const TREATMENT_CATEGORY_LABELS: Record<PatientHistoryTreatment['type'], string> = {
  vaccination: 'Vaccinations',
  prescription: 'Prescriptions',
  procedure: 'Procedures',
}

const TREATMENT_TYPE_LABELS: Record<PatientHistoryTreatment['type'], string> = {
  vaccination: 'Vaccination',
  prescription: 'Prescription',
  procedure: 'Procedure',
}

const SOAP_FIELDS = [
  { key: 'subjective', label: 'Subjective' },
  { key: 'objective', label: 'Objective' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'plan', label: 'Plan' },
] as const

/** A pet's clinical records on screen: the same history its PDF export holds. */
export function PetRecordsPage() {
  const { pet, member } = useOutletContext<PetScopeContext>()
  useDocumentTitle(`${possessive(pet.name)} records`)

  if (member.region !== REGION_IDS.US) return <NotFound />
  return <PetRecords petId={pet.id} petName={pet.name} region={member.region} />
}

function PetRecords({
  petId,
  petName,
  region,
}: {
  petId: string
  petName: string
  region: RegionId
}) {
  const records = useGetPetRecords(petId)
  const download = useDownloadPetHistory(petId)

  return (
    <PageContainer
      backLink={{ to: `${routes.account.pets.index}?pet=${petId}`, label: 'Back to your pets' }}
      headline={[
        { text: possessive(petName), color: 'yellow' },
        { text: 'records', color: 'orange' },
      ]}
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="self-start"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <FileDown />
            {download.isPending ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
          {download.isError && (
            <p role="alert" className="text-label-sm text-destructive">
              We couldn&apos;t download {possessive(petName)} records. Please try again in a moment.
            </p>
          )}
        </div>

        {records.isPending ? (
          <LoadingSpinner />
        ) : records.isError ? (
          <p role="alert" className="text-label-sm text-destructive">
            We couldn&apos;t load {possessive(petName)} records. Please try again in a moment.
          </p>
        ) : (
          <Records records={records.data} region={region} />
        )}
      </div>
    </PageContainer>
  )
}

function Records({ records, region }: { records: GetPetRecordsResponse; region: RegionId }) {
  const { latestVitals, vaccinations, activePrescriptions, timeline } = records

  if (!latestVitals && vaccinations.length === 0 && timeline.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-12">
        <FileText className="size-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No records yet.</p>
      </div>
    )
  }

  return (
    <>
      {latestVitals && (
        <Section title="Latest vitals">
          <Card>
            <Readings vitals={latestVitals} region={region} />
          </Card>
        </Section>
      )}

      {vaccinations.length > 0 && (
        <Section title="Vaccinations">
          {vaccinations.map((vx, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: read-only list in server order
            <Card key={i}>
              <p className="text-button">{vx.name}</p>
              <Meta>
                {[
                  vx.quantity,
                  formatEntryDate(vx.givenAt, vx.clinicId, region),
                  vx.vet && formatName(vx.vet),
                ]}
              </Meta>
              {vx.tagAndSite && <Meta>{[vx.tagAndSite]}</Meta>}
              {vx.reminders.map((r) => (
                <p key={r.target} className="text-label-sm">
                  Next {r.target} booster due{' '}
                  {formatCalendarDate(r.dueAt, resolveDateFormat(null, region))}
                </p>
              ))}
            </Card>
          ))}
        </Section>
      )}

      {activePrescriptions.length > 0 && (
        <Section title="Active prescriptions">
          {activePrescriptions.map((rx, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: read-only list in server order
            <Card key={i}>
              <p className="text-button">{rx.name}</p>
              <Meta>
                {[rx.instructions, rx.vet && formatName(rx.vet), formatRefills(rx.refills)]}
              </Meta>
            </Card>
          ))}
        </Section>
      )}

      {timeline.length > 0 && (
        <Section title="History">
          {timeline.map((entry, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: read-only list in server order
            <TimelineEntry key={i} entry={entry} region={region} />
          ))}
        </Section>
      )}
    </>
  )
}

function TimelineEntry({ entry, region }: { entry: PatientHistoryEntry; region: RegionId }) {
  switch (entry.entryType) {
    case 'consultation': {
      const title =
        (entry.appointmentTypeId && APPOINTMENT_TYPES_BY_ID[entry.appointmentTypeId]?.name) ||
        'Consultation'
      const location = entry.clinicId ? getClinic(entry.clinicId)?.name : 'Remote'
      const types = [...new Set(entry.treatments.map((t) => t.type))]
      return (
        <Card>
          <EntryHeader
            title={title}
            meta={[
              formatEntryDate(entry.at, entry.clinicId, region),
              entry.vet && formatName(entry.vet),
              location,
            ]}
          />
          {entry.vitals && <Readings vitals={entry.vitals} region={region} />}
          {entry.notes.map((note, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: read-only list in server order
            <NoteContent key={i} note={note} />
          ))}
          {types.map((type) => (
            <div key={type} className="flex flex-col gap-1">
              <p className="text-label-sm font-bold uppercase text-muted-foreground">
                {TREATMENT_CATEGORY_LABELS[type]}
              </p>
              {entry.treatments
                .filter((t) => t.type === type)
                .map((t, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: read-only list in server order
                  <TreatmentLine key={i} treatment={t} />
                ))}
            </div>
          ))}
        </Card>
      )
    }
    case 'note':
      return (
        <Card>
          <EntryHeader
            title={`${NOTE_TYPE_LABELS[entry.note.type]} note`}
            meta={[formatEntryDate(entry.at, null, region), entry.vet && formatName(entry.vet)]}
          />
          <NoteContent note={entry.note} />
        </Card>
      )
    case 'vitals':
      return (
        <Card>
          <EntryHeader title="Vitals" meta={[formatEntryDate(entry.at, null, region)]} />
          <Readings vitals={entry.vitals} region={region} />
        </Card>
      )
    case 'treatment':
      return (
        <Card>
          <EntryHeader
            title={TREATMENT_TYPE_LABELS[entry.treatment.type]}
            meta={[
              formatEntryDate(entry.at, entry.clinicId, region),
              entry.vet && formatName(entry.vet),
            ]}
          />
          <TreatmentLine treatment={entry.treatment} />
        </Card>
      )
  }
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-headline-sm">{title}</h2>
      {children}
    </section>
  )
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-2xl border-2 border-foreground bg-muted p-4">
      {children}
    </div>
  )
}

function EntryHeader({ title, meta }: { title: string; meta: (string | null | undefined)[] }) {
  return (
    <div className="flex flex-col">
      <p className="text-button">{title}</p>
      <Meta>{meta}</Meta>
    </div>
  )
}

function Meta({ children }: { children: (string | null | undefined)[] }) {
  return (
    <p className="text-label-sm text-muted-foreground">{children.filter(Boolean).join(' · ')}</p>
  )
}

function Readings({ vitals, region }: { vitals: PatientHistoryVitals; region: RegionId }) {
  const readings = [
    vitals.weight && ['Weight', formatWeight(Number.parseFloat(vitals.weight), region)],
    vitals.temperature && ['Temp', formatTemperature(Number(vitals.temperature), region)],
    vitals.heartRate && ['Heart rate', `${vitals.heartRate} bpm`],
    vitals.respiratoryRate && ['Breathing rate', `${vitals.respiratoryRate} rpm`],
    vitals.bodyConditionScore && ['Body condition', `${vitals.bodyConditionScore}/9`],
    vitals.painScore != null && ['Pain', `${vitals.painScore}/10`],
  ].filter((r): r is [string, string] => Array.isArray(r))

  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1 text-label-sm">
      {readings.map(([label, value]) => (
        <div key={label} className="flex gap-1">
          <dt className="font-bold">{label}:</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function TreatmentLine({ treatment }: { treatment: PatientHistoryTreatment }) {
  const refills = treatment.repeatsAllowed
  const details =
    treatment.type === 'procedure'
      ? []
      : [
          treatment.quantity,
          refills ? `${refills} refill${refills > 1 ? 's' : ''}` : null,
          treatment.instructions,
        ]
  return (
    <div>
      <p className="text-sm font-bold">{treatment.name}</p>
      {details.some(Boolean) && <Meta>{details}</Meta>}
    </div>
  )
}

function NoteContent({ note }: { note: PatientHistoryNote }) {
  if (note.type !== 'soap') return <RichText html={note.content.text} />
  return (
    <div className="flex flex-col gap-2">
      {SOAP_FIELDS.map(({ key, label }) => {
        const html = note.content[key]
        if (!html) return null
        return (
          <div key={key}>
            <p className="text-label-sm font-bold">{label}</p>
            <RichText html={html} />
          </div>
        )
      })}
    </div>
  )
}

/** Renders note HTML, which the API sanitises before sending. */
function RichText({ html }: { html: string }) {
  return (
    <div
      className="text-sm [&_a]:underline [&_b]:font-bold [&_em]:italic [&_i]:italic [&_li+li]:mt-0.5 [&_ol]:ml-4 [&_ol]:list-decimal [&_p+p]:mt-1 [&_strong]:font-bold [&_ul]:ml-4 [&_ul]:list-disc"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised server-side
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function formatRefills(refills: { allowed: number; remaining: number } | null) {
  if (!refills) return null
  return `${refills.remaining} of ${refills.allowed} ${refills.allowed === 1 ? 'refill' : 'refills'} remaining`
}

/** Dates an instant on the day it was at its clinic. */
function formatEntryDate(at: string, clinicId: string | null, region: RegionId) {
  return formatInstantDate(at, resolveDateFormat(clinicId, region))
}
